import { useCallback, useEffect, useRef, useState } from 'react'
import type { AssessmentOverview } from '@assessments/shared'
import { fetchAssessmentOverview } from './api.js'

export interface UseAssessmentOverview {
  data: AssessmentOverview | null
  loading: boolean
  error: string | null
  reload: () => void
}

/**
 * Loads the overview for an org-survey id. Refetches on id change and on window
 * focus, so completion changes made elsewhere (e.g. marking a section complete in
 * the section view) are reflected when the respondent returns here.
 */
export function useAssessmentOverview(organizationSurveyId: string): UseAssessmentOverview {
  const [data, setData] = useState<AssessmentOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Shared request identity: every load() bumps this, so any earlier in-flight
  // response (initial load or a prior focus refetch) is ignored once superseded.
  const requestId = useRef(0)
  const hasData = useRef(false)

  const load = useCallback(() => {
    const id = ++requestId.current
    // A refetch keeps the page rendered on the data it already has.
    if (!hasData.current) setLoading(true)
    setError(null)
    void (async () => {
      try {
        const overview = await fetchAssessmentOverview(organizationSurveyId)
        if (id === requestId.current) {
          hasData.current = true
          setData(overview)
        }
      } catch (err: unknown) {
        if (id === requestId.current) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    })()
    return () => {
      requestId.current++
    }
  }, [organizationSurveyId])

  useEffect(() => {
    hasData.current = false
    const cancel = load()
    function onFocus() {
      load()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      cancel()
      window.removeEventListener('focus', onFocus)
    }
  }, [load])

  // Until the effect fires for a new id, `data` still belongs to the previous assessment;
  // withhold it so consumers never evaluate another assessment's gate state.
  const currentData = data?.organizationSurveyId === organizationSurveyId ? data : null
  const currentLoading = loading || (data !== null && currentData === null)
  return { data: currentData, loading: currentLoading, error, reload: load }
}
